/**
 * KASHEER Theme — main JavaScript
 *
 * Vanilla JS only. All behaviour is attached with delegated listeners on
 * data-* attributes so it keeps working when the theme editor re-renders a
 * section. Cart updates use the Section Rendering API, so every piece of
 * cart HTML is produced by Liquid (correct money formatting, escaping and
 * translations) rather than by string templates here.
 */
(function () {
  'use strict';

  var routes = window.routes || {};
  var strings = window.cartStrings || {};
  var settings = window.kasheerSettings || {};

  var CART_URL = routes.cart_url || '/cart';
  var CART_ADD_URL = routes.cart_add_url || '/cart/add';
  var CART_CHANGE_URL = routes.cart_change_url || '/cart/change';
  var CART_UPDATE_URL = routes.cart_update_url || '/cart/update';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------ */
  /* Helpers                                                              */
  /* ------------------------------------------------------------------ */

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function on(type, selector, handler, options) {
    document.addEventListener(type, function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var match = target.closest(selector);
      if (match) handler(event, match);
    }, options || false);
  }

  function unique(list) {
    return list.filter(function (item, index) {
      return item && list.indexOf(item) === index;
    });
  }

  function parseJSONResponse(response) {
    return response.text().then(function (text) {
      var data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        data = {};
      }
      if (!response.ok) {
        var error = new Error(data.description || data.message || strings.error || 'Request failed');
        error.status = response.status;
        error.data = data;
        throw error;
      }
      return data;
    });
  }

  /**
   * Extract the inner HTML of a container inside a Section Rendering API
   * response, so the wrapper that holds UI state is never replaced.
   */
  function sectionInnerHTML(sections, id, selector) {
    if (!sections || !sections[id]) return null;
    var doc = new DOMParser().parseFromString(sections[id], 'text/html');
    var node = selector ? doc.querySelector(selector) : doc.body.firstElementChild;
    return node ? node.innerHTML : null;
  }

  /**
   * Format a price in cents the way Liquid's | money filter does for the
   * visitor's currency, for client-rendered prices such as predictive search.
   * The format comes from window.kasheerMoneySample (123456 | money, e.g.
   * "Rs. 1,234.56", "$1,234.56", "£1,234.56"); the store's own
   * shop.money_format is used only when the visitor pays in the store
   * currency, and Intl is the last resort.
   */
  var MONEY_SAMPLES = [
    ['1,234.56', 'amount'],
    ['1.234,56', 'amount_with_comma_separator'],
    ['1,235', 'amount_no_decimals'],
    ['1.235', 'amount_no_decimals_with_comma_separator']
  ];

  function moneyTemplate() {
    var sample = window.kasheerMoneySample;
    if (typeof sample === 'string') {
      for (var i = 0; i < MONEY_SAMPLES.length; i++) {
        if (sample.indexOf(MONEY_SAMPLES[i][0]) !== -1) {
          return sample.replace(MONEY_SAMPLES[i][0], '{{' + MONEY_SAMPLES[i][1] + '}}');
        }
      }
    }
    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    var format = window.kasheerMoneyFormat;
    if (typeof format === 'string' && format.indexOf('{{') !== -1 && (!active || active === window.kasheerShopCurrency)) {
      return format;
    }
    return null;
  }

  // Shopify's money placeholders always group the same way, whatever the
  // browser's language: {{amount}} is 1,234.56, the comma version 1.234,56.
  function groupAmount(amount, decimals, thousands, point) {
    var parts = amount.toFixed(decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return decimals ? whole + point + parts[1] : whole;
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var amount = Number(cents) / 100;
    var template = moneyTemplate();
    if (template) {
      return template
        .replace(/\{\{\s*amount\s*\}\}/g, groupAmount(amount, 2, ',', '.'))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, groupAmount(amount, 0, ',', ''))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, groupAmount(amount, 2, '.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, groupAmount(amount, 0, '.', ''));
    }
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || window.kasheerShopCurrency || 'INR';
    var locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency, currencyDisplay: 'symbol' }).format(amount);
    } catch (err) {
      return currency + ' ' + amount.toFixed(2);
    }
  }
  window.formatMoney = formatMoney;

  /* ------------------------------------------------------------------ */
  /* Cart state                                                           */
  /* ------------------------------------------------------------------ */

  function updateCartCount(count) {
    var value = count > 0 ? String(count) : '';
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = value;
    });
  }

  function cartSections() {
    return qsa('[data-cart-section][data-section-id]');
  }

  function cartSectionIds() {
    return unique(cartSections().map(function (el) {
      return el.getAttribute('data-section-id');
    }));
  }

  /** Replace the content of every cart section present in the response. */
  function renderCartSections(sections) {
    if (!sections) return;
    cartSections().forEach(function (section) {
      var id = section.getAttribute('data-section-id');
      var html = sectionInnerHTML(sections, id, '[data-cart-section-content]');
      var target = qs('[data-cart-section-content]', section);
      if (target && html !== null) {
        target.innerHTML = html;
        renderApprox(target);
      }
    });
    var countEl = qs('[data-cart-item-count]');
    if (countEl) updateCartCount(parseInt(countEl.textContent, 10) || 0);
  }

  function refreshCartSections() {
    var ids = cartSectionIds();
    if (!ids.length) return Promise.resolve();
    return fetch(CART_URL + '?sections=' + encodeURIComponent(ids.join(',')), {
      headers: { 'Accept': 'application/json' }
    })
      .then(parseJSONResponse)
      .then(renderCartSections)
      .catch(function (error) {
        console.error('[Kasheer] Could not refresh cart:', error);
      });
  }

  function showCartError(section, message) {
    var box = qs('[data-cart-error]', section);
    if (!box) {
      window.alert(message);
      return;
    }
    box.textContent = message;
    box.hidden = false;
  }

  var LINE_CONTROLS = ['data-qty-plus', 'data-qty-minus', 'data-qty-input', 'data-line-remove'];

  /**
   * Re-rendering a cart section replaces the control that has keyboard
   * focus, which would otherwise drop focus to <body> (and let it escape the
   * open drawer). Remember which control was active so it can be restored.
   */
  function rememberFocus() {
    var active = document.activeElement;
    if (!active || typeof active.closest !== 'function') return null;
    var line = active.closest('[data-line-key]');
    var control = null;
    LINE_CONTROLS.some(function (attr) {
      if (active.hasAttribute(attr)) {
        control = attr;
        return true;
      }
      return false;
    });
    return { key: line ? line.getAttribute('data-line-key') : null, control: control };
  }

  function restoreFocus(memo, section) {
    if (!memo) return;
    var target = null;
    if (memo.key && memo.control) {
      var line = qs('[data-line-key="' + memo.key + '"]', section || document);
      if (line) target = qs('[' + memo.control + ']', line);
    }
    if (!target && drawer.isOpen()) {
      target = qs('[data-cart-drawer-panel]', drawer.el());
    }
    if (!target && section) {
      target = qs(FOCUSABLE, section);
    }
    if (target && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  }

  var pendingChange = false;

  function changeLine(key, quantity, context) {
    if (pendingChange) return Promise.resolve();
    pendingChange = true;

    var section = context.closest('[data-cart-section]');
    var focusMemo = rememberFocus();
    if (section) section.classList.add('is-loading');

    return fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: key,
        quantity: Math.max(0, quantity),
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname
      })
    })
      .then(parseJSONResponse)
      .then(function (cart) {
        renderCartSections(cart.sections);
        updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('kasheer:cart:updated', { detail: cart }));
      })
      .catch(function (error) {
        console.error('[Kasheer] Cart update failed:', error);
        var message = error.message || strings.error || 'Cart update failed';
        return refreshCartSections().then(function () {
          if (section) showCartError(section, message);
        });
      })
      .then(function () {
        pendingChange = false;
        if (section) section.classList.remove('is-loading');
        restoreFocus(focusMemo, section);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer                                                          */
  /* ------------------------------------------------------------------ */

  var drawer = {
    lastFocus: null,

    el: function () {
      return qs('[data-cart-drawer]');
    },

    isOpen: function () {
      var el = this.el();
      return !!el && el.classList.contains('is-open');
    },

    open: function (trigger) {
      var el = this.el();
      if (!el) return;
      this.lastFocus = trigger || document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Focus the dialog itself (not the ×), so no focus ring shows after a
      // tap or click; keyboard users Tab from here to the close button.
      var panel = qs('[data-cart-drawer-panel]', el);
      window.setTimeout(function () {
        if (panel) panel.focus({ preventScroll: true });
      }, 50);
    },

    close: function () {
      var el = this.el();
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
      this.lastFocus = null;
    },

    trapFocus: function (event) {
      var el = this.el();
      var panel = el ? qs('[data-cart-drawer-panel]', el) : null;
      if (!panel) return;
      var focusables = qsa(FOCUSABLE, panel).filter(function (node) {
        return node.offsetParent !== null;
      });
      if (!focusables.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  on('click', '[data-cart-open]', function (event, trigger) {
    if (settings.cartDrawer === false || !drawer.el()) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  on('click', '[data-cart-close]', function (event) {
    event.preventDefault();
    drawer.close();
  });

  // Backwards compatibility with older section markup
  window.openCartDrawer = function () { drawer.open(); };
  window.closeCartDrawer = function () { drawer.close(); };

  /* ------------------------------------------------------------------ */
  /* Quantity controls & removal (drawer + cart page)                     */
  /* ------------------------------------------------------------------ */

  on('click', '[data-qty-plus], [data-qty-minus]', function (event, button) {
    var line = button.closest('[data-line-key]');
    if (!line) return;
    var input = qs('[data-qty-input]', line);
    var current = input ? parseInt(input.value, 10) || 0 : 1;
    var next = current + (button.hasAttribute('data-qty-plus') ? 1 : -1);
    changeLine(line.getAttribute('data-line-key'), next, line);
  });

  on('change', '[data-qty-input]', function (event, input) {
    var line = input.closest('[data-line-key]');
    if (!line) return;
    changeLine(line.getAttribute('data-line-key'), parseInt(input.value, 10) || 0, line);
  });

  on('click', '[data-line-remove]', function (event, button) {
    event.preventDefault();
    var line = button.closest('[data-line-key]');
    if (!line) return;
    changeLine(line.getAttribute('data-line-key'), 0, line);
  });

  // Order note (drawer): saved when the field is left, so a quantity change
  // that redraws the drawer keeps it. Checkout also submits it with the form.
  on('change', '[data-cart-note]', function (event, field) {
    fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: field.value })
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
    }).catch(function (error) {
      showCartError(field.closest('[data-cart-section]') || document, strings.error || 'The note could not be saved.');
      if (window.console && console.warn) console.warn('Kasheer: order note not saved', error);
    });
  });

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                          */
  /* ------------------------------------------------------------------ */

  // Every button that adds this form to the cart: the form's own submit button
  // and, on product pages, the phone's sticky bar button (outside the form).
  function cartButtons(form) {
    var buttons = qsa('[type="submit"]:not([data-buy-now])', form);
    if (form.id) buttons = buttons.concat(qsa('[form="' + form.id + '"][type="submit"]:not([data-buy-now])'));
    if (form.hasAttribute('data-product-form')) buttons = buttons.concat(qsa('[data-mobile-add-btn]'));
    return unique(buttons);
  }

  // idle | loading | added | error — the label and a thin progress line tell the
  // shopper something is happening; aria-busy tells assistive technology.
  function setCartButtons(buttons, state) {
    buttons.forEach(function (button) {
      // Capture the idle label fresh each time: variant changes can rewrite it between adds.
      if (state === 'loading' || !button.hasAttribute('data-idle-label')) button.setAttribute('data-idle-label', button.textContent.trim());
      var label = button.getAttribute('data-idle-label');
      if (state === 'loading') label = strings.adding || 'Adding…';
      if (state === 'added') label = strings.added || 'Added';
      if (state === 'error') label = strings.errorLabel || 'Error';
      button.textContent = label;
      button.classList.toggle('is-loading', state === 'loading');
      button.classList.toggle('is-added', state === 'added');
      button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
      button.disabled = state !== 'idle';
    });
  }

  // The error shows next to the button the shopper can see: under the form, or
  // in the phone's sticky bar when the inline form is hidden.
  function formErrorBox(form) {
    qsa('[data-form-error]').forEach(function (el) { el.hidden = true; });
    var host = form;
    if (!form.getClientRects().length) host = qs('.kasheer-product__mobile-bar') || form;
    var box = qs('[data-form-error]', host);
    if (box) return box;
    box = document.createElement('p');
    box.className = 'kasheer-form-error';
    box.setAttribute('role', 'alert');
    box.setAttribute('data-form-error', '');
    box.hidden = true;
    if (host === form) host.appendChild(box); else host.insertBefore(box, host.firstChild);
    return box;
  }

  on('submit', '[data-product-form]', function (event, form) {
    // A submit button carrying data-buy-now posts natively with return_to=/checkout.
    if (event.submitter && event.submitter.hasAttribute('data-buy-now')) return;

    event.preventDefault();

    var buttons = cartButtons(form);
    if (buttons.some(function (b) { return b.classList.contains('is-loading'); })) return; // already adding
    var errorBox = formErrorBox(form);
    setCartButtons(buttons, 'loading');
    var opener = event.submitter || buttons[0] || null;

    var formData = new FormData(form);
    var ids = cartSectionIds();
    if (ids.length) {
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    fetch(CART_ADD_URL, {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(parseJSONResponse)
      .then(function (item) {
        setCartButtons(buttons, 'added');
        var rendered = false;
        if (item.sections) {
          renderCartSections(item.sections);
          rendered = true;
        }
        var afterRender = rendered ? Promise.resolve() : refreshCartSections();
        return afterRender.then(function () {
          document.dispatchEvent(new CustomEvent('kasheer:cart:added', { detail: item }));
          if (settings.cartDrawer !== false) drawer.open(opener);
          window.setTimeout(function () { setCartButtons(buttons, 'idle'); }, 1500);
        });
      })
      .catch(function (error) {
        console.error('[Kasheer] Add to cart failed:', error);
        errorBox.textContent = error.message || strings.error || 'Could not add to cart';
        errorBox.hidden = false;
        setCartButtons(buttons, 'error');
        window.setTimeout(function () { setCartButtons(buttons, 'idle'); }, 2000);
      });
  });

  /* ------------------------------------------------------------------ */
  /* Product: variant selection (sections that opt in via data-product-section) */
  /* ------------------------------------------------------------------ */

  function readVariants(section) {
    var node = qs('[data-product-variants]', section);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[Kasheer] Invalid variant JSON', error);
      return [];
    }
  }

  function updateVariant(section) {
    var selects = qsa('[data-variant-option]', section);
    if (!selects.length) return;

    var selected = selects.map(function (select) { return select.value; });
    var match = readVariants(section).find(function (variant) {
      return variant.options.every(function (value, index) { return value === selected[index]; });
    });

    var idInput = qs('[data-variant-id]', section);
    var addButton = qs('[data-add-to-cart-btn]', section);
    var buyNow = qs('[data-buy-now]', section);
    var price = qs('[data-product-price]', section);
    var availability = qs('[data-product-availability]', section);

    if (!match) {
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = addButton.getAttribute('data-label-unavailable') || strings.unavailable || 'Unavailable';
      }
      if (buyNow) buyNow.disabled = true;
      return;
    }

    if (idInput) idInput.value = match.id;
    if (price) price.textContent = match.price_formatted;

    if (match.available) {
      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = addButton.getAttribute('data-label-add') || addButton.textContent;
      }
      if (buyNow) buyNow.disabled = false;
      if (availability) availability.textContent = availability.getAttribute('data-label-available') || '';
    } else {
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = addButton.getAttribute('data-label-sold-out') || strings.soldOut || 'Sold out';
      }
      if (buyNow) buyNow.disabled = true;
      if (availability) availability.textContent = availability.getAttribute('data-label-unavailable') || '';
    }

    var url = new URL(window.location.href);
    url.searchParams.set('variant', match.id);
    window.history.replaceState({}, '', url.toString());
  }

  on('change', '[data-variant-option]', function (event, select) {
    var section = select.closest('[data-product-section]');
    if (section) updateVariant(section);
  });

  /* ------------------------------------------------------------------ */
  /* Header panels (sections that use data-panel-toggle / data-panel)     */
  /* ------------------------------------------------------------------ */

  function closePanels(except) {
    qsa('[data-panel]').forEach(function (panel) {
      if (panel !== except) panel.classList.remove('is-open');
    });
    qsa('[data-panel-toggle]').forEach(function (button) {
      var panel = qs('[data-panel="' + button.getAttribute('data-panel-toggle') + '"]');
      var open = !!panel && panel.classList.contains('is-open');
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = open ? button.getAttribute('data-label-close') : button.getAttribute('data-label-open');
      if (label) button.setAttribute('aria-label', label);
    });
  }

  on('click', '[data-panel-toggle]', function (event, button) {
    var name = button.getAttribute('data-panel-toggle');
    var panel = qs('[data-panel="' + name + '"]');
    if (!panel) return;
    var willOpen = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', willOpen);
    closePanels(willOpen ? panel : null);
    if (willOpen) {
      var input = qs('input:not([type="hidden"])', panel);
      if (input) window.setTimeout(function () { input.focus(); }, 50);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Customer account helpers                                             */
  /* ------------------------------------------------------------------ */

  on('click', '[data-toggle-target]', function (event, button) {
    event.preventDefault();
    var target = document.getElementById(button.getAttribute('data-toggle-target'));
    if (!target) return;
    var show = target.hidden;
    target.hidden = !show;
    button.setAttribute('aria-expanded', show ? 'true' : 'false');
    if (show) {
      var first = qs(FOCUSABLE, target);
      if (first) first.focus();
    }
  });

  on('click', '[data-show-target]', function (event, button) {
    event.preventDefault();
    var show = document.getElementById(button.getAttribute('data-show-target'));
    var hide = document.getElementById(button.getAttribute('data-hide-target'));
    if (hide) hide.hidden = true;
    if (show) {
      show.hidden = false;
      var first = qs(FOCUSABLE, show);
      if (first) first.focus();
    }
    if (button.hasAttribute('data-set-hash')) {
      window.history.replaceState({}, '', button.getAttribute('data-set-hash') || window.location.pathname);
    }
  });

  on('submit', 'form[data-confirm]', function (event, form) {
    if (!window.confirm(form.getAttribute('data-confirm'))) event.preventDefault();
  });

  /* ------------------------------------------------------------------ */
  /* Lazy-hydrate below-fold sections ([data-lazy-section])               */
  /* ------------------------------------------------------------------ */

  function initLazySections(root) {
    var lazySections = qsa('[data-lazy-section]:not([data-lazy-section-visible])', root);
    if (!lazySections.length) return;
    if (!('IntersectionObserver' in window)) {
      lazySections.forEach(function (el) { el.setAttribute('data-lazy-section-visible', ''); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-lazy-section-visible', '');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });
    lazySections.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------ */
  /* Product card photo sliders ([data-card-slider], card-media snippet)  */
  /* ------------------------------------------------------------------ */

  var reduceCardMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function cardIndex(track) {
    return Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
  }

  // Product / Model photos: 'model' or 'product', kept on <html> (set before
  // the first paint in layout/theme.liquid). Slides and dashes tagged with the
  // other view are not displayed.
  function currentCardView() {
    return document.documentElement.getAttribute('data-card-view') === 'product' ? 'product' : 'model';
  }

  function inCurrentView(el) {
    var only = el.getAttribute('data-card-view-only');
    return !only || only === currentCardView();
  }

  function visibleSlides(track) {
    return Array.prototype.filter.call(track.children, inCurrentView);
  }

  function updateCardDashes(slider) {
    var track = qs('[data-card-track]', slider);
    var row = slider.nextElementSibling;
    if (!track || !row || !row.classList.contains('kasheer-card-dashes')) return;
    var index = cardIndex(track);
    qsa('span', row).filter(inCurrentView).forEach(function (dash, n) {
      dash.classList.toggle('is-active', n === index);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Archive video tiles (snippets/archive-tile.liquid)                   */
  /* ------------------------------------------------------------------ */

  // Silent loops that play only while on screen, so a page of tiles never
  // downloads or plays video out of view. With "reduce motion" on, the cover
  // photo (or the video's first frame) stays still.
  var tileVideoObserver = null;

  function playTileVideo(video) {
    video.muted = true;
    if (video.preload !== 'auto') video.preload = 'auto';
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function (error) {
        // iOS Low Power Mode and some data-saver settings refuse autoplay;
        // the cover then simply stays, which is the intended fallback.
        if (window.console && console.debug) console.debug('Kasheer: tile video did not autoplay', error && error.name);
      });
    }
  }

  function initTileVideos(root) {
    var holders = qsa('[data-tile-video]', root).filter(function (holder) {
      return !holder.hasAttribute('data-tile-video-ready');
    });
    if (!holders.length) return;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    holders.forEach(function (holder) {
      holder.setAttribute('data-tile-video-ready', '');
      var video = qs('video', holder);
      if (!video) return;
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.addEventListener('playing', function () { holder.classList.add('is-playing'); });
    });

    if (reduceMotion || !('IntersectionObserver' in window)) return;
    if (!tileVideoObserver) {
      tileVideoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = qs('video', entry.target);
          if (!video) return;
          if (entry.isIntersecting) playTileVideo(video);
          else if (!video.paused) video.pause();
        });
      }, { threshold: 0.25 });
    }
    holders.forEach(function (holder) { tileVideoObserver.observe(holder); });
  }

  function initCardSliders(root) {
    qsa('[data-card-slider]', root).forEach(function (slider) {
      if (slider.dataset.cardReady === 'true') return;
      slider.dataset.cardReady = 'true';
      var track = qs('[data-card-track]', slider);
      if (!track) return;
      var ticking = false;

      function update() {
        ticking = false;
        updateCardDashes(slider);
      }

      // Load the other photos of the set on show as soon as someone shows
      // interest in the card, so a swipe or an arrow click never lands on an
      // empty frame. The other view's photos wait until a visitor switches.
      function warm() {
        visibleSlides(track).forEach(function (slide) {
          qsa('img[loading="lazy"]', slide).forEach(function (img) { img.loading = 'eager'; });
        });
      }

      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      }, { passive: true });
      slider.addEventListener('pointerenter', warm);
      track.addEventListener('touchstart', warm, { passive: true });
      update();
    });
  }

  on('click', '[data-card-prev], [data-card-next]', function (event, button) {
    event.preventDefault();
    event.stopPropagation();
    var slider = button.closest('[data-card-slider]');
    var track = slider ? qs('[data-card-track]', slider) : null;
    if (!track) return;
    var count = visibleSlides(track).length;
    var target = cardIndex(track) + (button.hasAttribute('data-card-next') ? 1 : -1);
    if (target >= count) target = 0;
    if (target < 0) target = count - 1;
    track.scrollTo({ left: target * track.clientWidth, behavior: reduceCardMotion ? 'auto' : 'smooth' });
  });

  /* ------------------------------------------------------------------ */
  /* Product / Model switch (snippets/card-view-switch.liquid)            */
  /* ------------------------------------------------------------------ */

  var CARD_VIEW_KEY = 'kasheer-card-view';
  var CARD_VIEW_OUT_MS = 300;   // --kasheer-dur-out: the photos fade out first
  var CARD_VIEW_WAIT_MS = 700;  // longest wait for the new photos before fading back in
  var cardViewRun = 0;

  function syncViewSwitches() {
    var view = currentCardView();
    qsa('[data-card-view-set]').forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-card-view-set') === view));
    });
  }

  function onScreen(el) {
    var rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0;
  }

  // Resolves once the first photo of each card on screen can be shown, or
  // after CARD_VIEW_WAIT_MS, whichever comes first.
  function firstPhotosReady(boxes) {
    var waits = [];
    boxes.forEach(function (box) {
      if (!onScreen(box)) return;
      var track = qs('[data-card-track]', box);
      var slide = track ? visibleSlides(track)[0] : null;
      var img = slide ? qs('img', slide) : null;
      if (!img || img.complete) return;
      img.loading = 'eager';
      waits.push(img.decode ? img.decode().catch(function () {}) : Promise.resolve());
    });
    var timeout = new Promise(function (resolve) { window.setTimeout(resolve, CARD_VIEW_WAIT_MS); });
    return Promise.race([Promise.all(waits), timeout]);
  }

  function showCardView(view, boxes) {
    document.documentElement.setAttribute('data-card-view', view);
    boxes.forEach(function (box) {
      var track = qs('[data-card-track]', box);
      if (track) track.scrollLeft = 0;
      if (box.hasAttribute('data-card-slider')) updateCardDashes(box);
    });
    syncViewSwitches();
  }

  function setCardView(view) {
    if (view !== 'model' && view !== 'product') return;
    try { window.localStorage.setItem(CARD_VIEW_KEY, view); } catch (error) { /* private mode: the choice lasts for this page only */ }
    var boxes = qsa('[data-card-dual]');
    var run = ++cardViewRun;
    if (reduceCardMotion || !boxes.length) {
      showCardView(view, boxes);
      return;
    }
    // Fade out, change the set, wait for the new first photos, glide back in
    boxes.forEach(function (box) { box.classList.add('is-view-out'); });
    window.setTimeout(function () {
      if (run !== cardViewRun) return;
      showCardView(view, boxes);
      firstPhotosReady(boxes).then(function () {
        if (run !== cardViewRun) return;
        boxes.forEach(function (box) { box.classList.remove('is-view-out'); });
      });
    }, CARD_VIEW_OUT_MS);
  }

  // Either word flips the view, so the pair works as one on/off button
  on('click', '[data-card-view-set]', function (event, button) {
    event.preventDefault();
    var view = button.getAttribute('data-card-view-set');
    if (view === currentCardView()) view = view === 'model' ? 'product' : 'model';
    setCardView(view);
  });

  /* ------------------------------------------------------------------ */
  /* Visitor's country (Shopify's own location lookup)                    */
  /* ------------------------------------------------------------------ */

  var DETECTED_COUNTRY_KEY = 'kasheer-detected-country';
  var COUNTRY_CHOSEN_KEY = 'kasheer-country-chosen';
  var detectedCountry = null; // one lookup per page, shared by every caller

  function storageGet(area, key) {
    try { return window[area].getItem(key); } catch (error) { return null; }
  }

  function storageSet(area, key, value) {
    try { window[area].setItem(key, value); } catch (error) { /* private mode: only this page remembers */ }
  }

  // Where Shopify thinks the visitor is: the lookup its Geolocation app uses,
  // asked once per visit. Resolves to a country code such as "GB", or null.
  function lookupCountry() {
    if (detectedCountry) return detectedCountry;
    var cached = storageGet('sessionStorage', DETECTED_COUNTRY_KEY);
    if (cached) {
      detectedCountry = Promise.resolve(cached);
      return detectedCountry;
    }
    var root = ((window.routes && window.routes.root_url) || '/').replace(/\/?$/, '/');
    detectedCountry = fetch(root + 'browsing_context_suggestions.json?country[enabled]=true', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var country = data && data.detected_values && data.detected_values.country;
        var code = country && country.handle ? String(country.handle).toUpperCase() : null;
        if (code) storageSet('sessionStorage', DETECTED_COUNTRY_KEY, code);
        return code;
      })
      .catch(function (error) {
        console.warn('[Kasheer] Country lookup failed:', error);
        return null;
      });
    return detectedCountry;
  }

  /* ------------------------------------------------------------------ */
  /* Approximate prices outside India (snippets/approx-price.liquid)      */
  /* ------------------------------------------------------------------ */

  var APPROX_SYMBOLS = { USD: '$', GBP: '£' };

  // When the store sells to several countries, the visitor's chosen (or
  // auto-switched) country decides; with India only, Shopify calls every
  // visitor "India", so ask where they really are.
  function approxCountry(config) {
    return config.multi ? Promise.resolve(config.country || null) : lookupCountry();
  }

  function approxCurrency(country, config) {
    if (!country || country === config.home) return null;
    return country === 'GB' ? 'GBP' : 'USD';
  }

  function approxText(cents, currency, config, withNote) {
    var rate = Number(config.rates && config.rates[currency]);
    if (!(rate > 0) || !(cents >= 0)) return '';
    var step = config.step > 0 ? config.step : 1;
    var exact = cents / 100 / rate;
    // Round to the step (5 by default); below one step round to the whole
    // unit instead, so a small price is never overstated. Nothing under 0.5.
    var amount = exact < step ? Math.round(exact) : Math.round(exact / step) * step;
    if (amount <= 0) return '';
    var text = '≈ ' + APPROX_SYMBOLS[currency] + groupAmount(amount, 0, ',', '');
    return withNote && config.note ? text + ' · ' + config.note : text;
  }

  function renderApprox(root) {
    var config = window.kasheerApprox;
    var lines = qsa('[data-approx-cents]', root);
    if (!config || !config.enabled || !lines.length) return;
    approxCountry(config).then(function (country) {
      var currency = approxCurrency(country, config);
      lines.forEach(function (line) {
        var cents = Number(line.getAttribute('data-approx-cents'));
        var text = currency ? approxText(cents, currency, config, line.hasAttribute('data-approx-note')) : '';
        line.textContent = text;
        line.hidden = !text;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Country picker (snippets/country-picker.liquid)                      */
  /* ------------------------------------------------------------------ */

  function setPickerOpen(picker, open) {
    var toggle = qs('[data-country-toggle]', picker);
    var panel = qs('[data-country-panel]', picker);
    if (!toggle || !panel) return;
    // Closing with focus still inside (Escape, or a click on empty space):
    // hand focus back to the toggle so keyboard users keep their place
    if (!open && panel.contains(document.activeElement)) toggle.focus();
    toggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (open) {
      var search = qs('[data-country-search]', panel);
      if (search) search.focus();
    }
  }

  function closePickers(except) {
    qsa('.kasheer-country--dropdown').forEach(function (picker) {
      if (picker !== except) setPickerOpen(picker, false);
    });
  }

  on('click', '[data-country-toggle]', function (event, toggle) {
    event.preventDefault();
    var picker = toggle.closest('[data-country-picker]');
    var open = toggle.getAttribute('aria-expanded') !== 'true';
    closePickers(picker);
    setPickerOpen(picker, open);
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (target && typeof target.closest === 'function' && target.closest('[data-country-picker]')) return;
    closePickers(null);
  });

  on('input', '[data-country-search]', function (event, input) {
    var panel = input.closest('[data-country-panel]');
    var query = input.value.trim().toLowerCase();
    var shown = 0;
    qsa('[data-country-name]', panel).forEach(function (item) {
      var match = !query || item.getAttribute('data-country-name').indexOf(query) !== -1;
      item.hidden = !match;
      if (match) shown += 1;
    });
    var empty = qs('[data-country-empty]', panel);
    if (empty) empty.hidden = shown > 0;
  });

  // Enter in the search box picks the first country still listed
  on('keydown', '[data-country-search]', function (event, input) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    var first = qsa('[data-country-name]', input.closest('[data-country-panel]')).filter(function (item) {
      return !item.hidden;
    })[0];
    var button = first ? qs('button', first) : null;
    if (button) button.click();
  });

  on('change', '[data-country-select]', function (event, select) {
    storageSet('localStorage', COUNTRY_CHOSEN_KEY, 'picked');
    if (select.form) select.form.submit();
  });

  on('submit', '.kasheer-country__form', function () {
    storageSet('localStorage', COUNTRY_CHOSEN_KEY, 'picked');
  });

  // First visit: switch once to the visitor's own country (as Rastah does),
  // unless they have chosen one before. Not in the theme editor.
  function autoSwitchCountry() {
    var picker = qs('[data-country-picker]');
    if (!picker || storageGet('localStorage', COUNTRY_CHOSEN_KEY)) return;
    if (window.Shopify && window.Shopify.designMode) return;
    lookupCountry().then(function (code) {
      storageSet('localStorage', COUNTRY_CHOSEN_KEY, 'auto');
      var form = qs('form', picker);
      if (!code || !form || code === picker.getAttribute('data-current-country')) return;
      var offered = qsa('option, button[name="country_code"]', picker).some(function (el) {
        return el.value === code;
      });
      if (!offered) return;
      var select = qs('select[name="country_code"]', form);
      if (select) select.disabled = true;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'country_code';
      input.value = code;
      form.appendChild(input);
      form.submit();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cookie notice (snippets/cookie-notice.liquid)                        */
  /* ------------------------------------------------------------------ */

  var COOKIE_CHOICE_KEY = 'kasheer-cookie-choice'; // 'accept' | 'decline', a backup of Shopify's record
  var COOKIE_LATER_KEY = 'kasheer-cookie-later';   // closed with ×: ask again next visit
  var COOKIE_SHOW_DELAY = 1200;                    // let the page settle before the panel glides in
  var COOKIE_HIDE_MS = 400;
  var privacyApi = null;

  // Shopify's Customer Privacy API, loaded once. Resolves to
  // window.Shopify.customerPrivacy, or null outside a Shopify storefront.
  function loadPrivacyApi() {
    if (privacyApi) return privacyApi;
    privacyApi = new Promise(function (resolve) {
      var shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        resolve(null);
        return;
      }
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (error) {
          console.warn('[Kasheer] Customer Privacy API did not load:', error);
          resolve(null);
          return;
        }
        resolve(window.Shopify.customerPrivacy || null);
      });
    });
    return privacyApi;
  }

  function cookieChoiceMade(api) {
    if (api && typeof api.currentVisitorConsent === 'function') {
      var consent = api.currentVisitorConsent() || {};
      if (consent.analytics || consent.marketing) return true; // 'yes' or 'no'; '' = not asked yet
    }
    return !!storageGet('localStorage', COOKIE_CHOICE_KEY);
  }

  function shouldShowCookieNotice(panel, api) {
    if (storageGet('sessionStorage', COOKIE_LATER_KEY)) return false;
    if (panel.getAttribute('data-audience') === 'regions') {
      return !!(api && typeof api.shouldShowBanner === 'function' && api.shouldShowBanner());
    }
    return !cookieChoiceMade(api);
  }

  // On phones the product page's buy bar is fixed to the bottom: sit the
  // panel just above it so "Add to archive" stays reachable.
  function cookieLift() {
    var bar = qs('.kasheer-product__mobile-bar');
    if (!bar || window.getComputedStyle(bar).display === 'none') return 0;
    return Math.round(bar.getBoundingClientRect().height);
  }

  function showCookieNotice(panel) {
    panel.style.setProperty('--kasheer-cookie-lift', cookieLift() + 'px');
    panel.hidden = false;
    panel.getBoundingClientRect(); // commit the start position before the glide
    panel.classList.add('is-open');
  }

  function hideCookieNotice(panel) {
    if (!panel) return;
    if (panel.contains(document.activeElement)) document.activeElement.blur();
    panel.classList.remove('is-open');
    window.setTimeout(function () {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    }, reduceCardMotion ? 0 : COOKIE_HIDE_MS);
  }

  function saveCookieChoice(accepted) {
    storageSet('localStorage', COOKIE_CHOICE_KEY, accepted ? 'accept' : 'decline');
    return loadPrivacyApi().then(function (api) {
      if (!api || typeof api.setTrackingConsent !== 'function') return null;
      return new Promise(function (resolve) {
        api.setTrackingConsent({
          analytics: accepted,
          marketing: accepted,
          preferences: accepted,
          sale_of_data: accepted
        }, resolve);
      });
    }).catch(function (error) {
      console.warn('[Kasheer] Could not save the cookie choice:', error);
    });
  }

  function initCookieNotice() {
    var panel = qs('[data-cookie-notice]');
    if (!panel || (window.Shopify && window.Shopify.designMode)) return;
    loadPrivacyApi().then(function (api) {
      if (!shouldShowCookieNotice(panel, api)) return;
      window.setTimeout(function () {
        if (shouldShowCookieNotice(panel, api)) showCookieNotice(panel);
      }, COOKIE_SHOW_DELAY);
    });
  }

  on('click', '[data-cookie-choice]', function (event, button) {
    saveCookieChoice(button.getAttribute('data-cookie-choice') === 'accept');
    hideCookieNotice(button.closest('[data-cookie-notice]'));
  });

  on('click', '[data-cookie-close]', function (event, button) {
    storageSet('sessionStorage', COOKIE_LATER_KEY, '1');
    hideCookieNotice(button.closest('[data-cookie-notice]'));
  });

  // Footer "Cookie preferences": open the panel again to change the answer
  on('click', '[data-cookie-preferences]', function (event) {
    event.preventDefault();
    var panel = qs('[data-cookie-notice]');
    if (!panel) return;
    showCookieNotice(panel);
    var first = qs('[data-cookie-choice]', panel);
    if (first) first.focus();
  });

  /* ------------------------------------------------------------------ */
  /* Keyboard                                                             */
  /* ------------------------------------------------------------------ */

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      if (drawer.isOpen()) {
        drawer.close();
      } else {
        closePickers(null);
        closePanels(null);
        var cookiePanel = qs('[data-cookie-notice].is-open');
        if (cookiePanel && cookiePanel.contains(document.activeElement)) {
          storageSet('sessionStorage', COOKIE_LATER_KEY, '1');
          hideCookieNotice(cookiePanel);
        }
      }
      return;
    }
    if (event.key === 'Tab' && drawer.isOpen()) {
      drawer.trapFocus(event);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  function init(root) {
    // The product gallery runs from assets/kasheer-gallery.js (product pages only)
    qsa('[data-product-section]', root).forEach(function (section) {
      if (qsa('[data-variant-option]', section).length) updateVariant(section);
    });
    initLazySections(root);
    initCardSliders(root);
    renderApprox(root);
    syncViewSwitches();
    initTileVideos(root);
    var drawerEl = drawer.el();
    if (drawerEl) drawerEl.setAttribute('aria-hidden', drawerEl.classList.contains('is-open') ? 'false' : 'true');

    // Login page: open the recovery form when linked with #recover
    if (window.location.hash === '#recover') {
      var recover = document.getElementById('RecoverPassword');
      var login = document.getElementById('CustomerLogin');
      if (recover) recover.hidden = false;
      if (login) login.hidden = true;
    }
  }

  init(document);
  autoSwitchCountry();
  initCookieNotice();

  // iOS Safari only shows :active styles (tap feedback) once the page listens for touches.
  document.addEventListener('touchstart', function () {}, { passive: true });

  // Theme editor: re-run initialisation for a section that was re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.Kasheer = {
    openCart: function () { drawer.open(); },
    closeCart: function () { drawer.close(); },
    refreshCart: refreshCartSections,
    renderApprox: renderApprox
  };
})();
